import React from 'react';

import { Box, Stack, Typography } from '@mui/material';

import type { Lens } from '~/types/leLens';

import LensStructuralProposalPreview from './LensStructuralProposalPreview';
import { CANVAS_COLUMNS } from './canvasLayout';
import type { LensProposal } from './lensProposal';
import { readRowsDrawerChoices } from './lensRowsDrawerStorage';
import { nativeDraftStructure } from './nativeDraftStructure';

/** A separate draft drawing: never an overlay on the saved board or a manual editor draft. */
export default function LensNativeDraftStructurePreview({
  lens,
  definition,
}: {
  lens: Lens;
  definition: NonNullable<LensProposal['definition']>;
}) {
  const expandedTableCardIds = new Set(
    Object.entries(readRowsDrawerChoices(lens.id))
      .filter(([, expanded]) => expanded)
      .map(([id]) => id),
  );
  const structure = nativeDraftStructure(definition, expandedTableCardIds);
  return (
    <Stack spacing={1.5} data-testid="lens-native-draft-structure-preview">
      <Typography variant="subtitle2">Proposed Lens structure</Typography>
      <Typography variant="body2" color="text.secondary">
        Review the draft here. The board still shows the saved Lens until you choose Save changes.
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Positions show how the board arranges each section. All proposed changes keeps the original draft values.
      </Typography>
      <Typography variant="h6">{structure.title}</Typography>
      {structure.description && <Typography variant="body2">{structure.description}</Typography>}
      {structure.sections.map((section) => (
        <Stack key={section.id} spacing={0.5}>
          <Typography variant="subtitle2">{section.title}</Typography>
          {section.cards.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No cards in this section
            </Typography>
          ) : (
            <Stack spacing={1}>
              <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                <Box
                  aria-hidden
                  data-native-structure-diagram=""
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: `repeat(${CANVAS_COLUMNS}, minmax(0, 1fr))`,
                    gridAutoRows: '12px',
                    gap: '2px',
                    minWidth: 240,
                  }}
                >
                  {section.cards.map((card, index) => (
                    <Box
                      key={card.id}
                      sx={{
                        gridColumn: `${card.layout.x + 1} / span ${card.layout.w}`,
                        gridRow: `${card.layout.y + 1} / span ${card.layout.h}`,
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 0.5,
                        display: 'grid',
                        placeItems: 'center',
                        fontSize: 10,
                        lineHeight: 1,
                        overflow: 'hidden',
                        bgcolor: 'background.paper',
                      }}
                    >
                      {index + 1}
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box component="ol" data-native-structure-legend="" sx={{ m: 0, pl: 3 }}>
                {section.cards.map((card) => (
                  <Box component="li" key={card.id} sx={{ mb: 0.5 }}>
                    <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                      {card.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Rendered placement: columns {card.layout.x + 1}–{card.layout.x + card.layout.w}, rows{' '}
                      {card.layout.y + 1}–{card.layout.y + card.layout.h}
                    </Typography>
                    {card.automatic && (
                      <Typography variant="caption" display="block">
                        Automatic placement
                      </Typography>
                    )}
                    {card.type === 'table' && !expandedTableCardIds.has(card.id) && (
                      <Typography variant="caption" display="block">
                        Table rows collapsed
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            </Stack>
          )}
        </Stack>
      ))}
      <Typography variant="body2" color="text.secondary">
        Chart values are not loaded in this structure preview.
      </Typography>
      <details>
        <summary>All proposed changes</summary>
        <LensStructuralProposalPreview lens={lens} definition={definition} reviewKind="draft" />
      </details>
    </Stack>
  );
}
